import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from './ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Searchable combobox built on shadcn Command + Popover.
 * items: [{ value, label, sublabel?, keywords? (array of strings for extra matching) }]
 * extraOption?: { value: string, label: node, onSelect: () => void } — always shown at bottom
 * clearable: if true, shows an X to clear the selection (calls onChange(''))
 */
const Combobox = ({
  value,
  onChange,
  items,
  placeholder = 'Select…',
  emptyText = 'nothing found',
  extraOption = null,
  disabled = false,
  clearable = true,
  className = '',
  triggerClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => items.find((i) => i.value === value), [items, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'w-full inline-flex items-center justify-between gap-2 rounded-md border border-input bg-white px-3 py-2 text-sm h-10 disabled:opacity-50 disabled:cursor-not-allowed hover:border-slate-300',
            triggerClassName
          )}
        >
          <span className={cn('truncate text-left flex-1', selected ? 'text-slate-800' : 'text-slate-400')}>
            {selected ? selected.label : placeholder}
          </span>
          {clearable && selected && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onChange(''); }}
              className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          ) : (
            <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn('p-0 w-[--radix-popover-trigger-width]', className)} align="start">
        <Command
          filter={(itemValue, search) => {
            if (!search) return 1;
            const it = items.find((x) => x.value === itemValue);
            if (!it) return itemValue === '__extra__' ? 1 : 0;
            const hay = [it.label, it.sublabel, ...(it.keywords || [])].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="search…" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((it) => (
                <CommandItem
                  key={it.value}
                  value={it.value}
                  onSelect={() => { onChange(it.value); setOpen(false); }}
                >
                  <Check className={cn('w-4 h-4 mr-2', value === it.value ? 'opacity-100 text-mint-700' : 'opacity-0')} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{it.label}</div>
                    {it.sublabel && <div className="text-[11px] text-slate-500 truncate">{it.sublabel}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {extraOption && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem value="__extra__" onSelect={() => { setOpen(false); extraOption.onSelect(); }}>
                    {extraOption.label}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default Combobox;
